import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import './ParagonEditor.css';

// ── Image upload context (passes onImageUpload into the NodeView) ─────────────

const ImageUploadContext = createContext(null);

// ── Resizable image node view ─────────────────────────────────────────────────

function ImageNodeView({ node, updateAttributes, selected, deleteNode }) {
  const onImageUpload = useContext(ImageUploadContext);
  const imgRef       = useRef(null);
  const replaceRef   = useRef(null);
  const drag         = useRef({ x: 0, w: 0 });
  const [broken, setBroken] = useState(false);

  function onResizeMouseDown(e) {
    e.preventDefault();
    e.stopPropagation();
    drag.current = {
      x: e.clientX,
      w: imgRef.current?.offsetWidth ?? node.attrs.width ?? 300,
    };

    const onMove = ev => {
      const w = Math.max(80, drag.current.w + ev.clientX - drag.current.x);
      updateAttributes({ width: w });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  async function handleReplaceFile(e) {
    const file = e.target.files?.[0];
    if (!file || !onImageUpload) return;
    e.target.value = '';
    try {
      const url = await onImageUpload(file);
      if (url) {
        updateAttributes({ src: url, width: null });
        setBroken(false);
      }
    } catch { /* silent — upload errors handled upstream */ }
  }

  const classes = `pe-img-wrap${selected ? ' pe-img-selected' : ''}${broken ? ' pe-img-broken' : ''}`;

  return (
    <NodeViewWrapper className={classes} data-drag-handle>
      {broken ? (
        <div
          className="pe-img-placeholder"
          title={onImageUpload ? 'Click to replace image' : undefined}
          onClick={() => onImageUpload && replaceRef.current?.click()}
        >
          <span>{onImageUpload ? 'Image unavailable — click to replace' : 'Image unavailable'}</span>
          <code className="pe-img-src-hint">{node.attrs.src}</code>
        </div>
      ) : (
        <img
          ref={imgRef}
          src={node.attrs.src}
          alt={node.attrs.alt ?? ''}
          draggable={false}
          style={node.attrs.width ? { width: `${node.attrs.width}px` } : undefined}
          onError={() => setBroken(true)}
        />
      )}

      <button
        type="button"
        className="pe-img-del"
        onMouseDown={e => { e.preventDefault(); deleteNode(); }}
      >✕</button>

      {!broken && selected && (
        <div className="pe-img-resize" onMouseDown={onResizeMouseDown} title="Drag to resize" />
      )}

      {onImageUpload && (
        <input
          ref={replaceRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleReplaceFile}
        />
      )}
    </NodeViewWrapper>
  );
}

// Image extension with width attribute + NodeView
const ResizableImage = Image.extend({
  draggable: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: el => parseInt(el.style.width) || null,
        renderHTML: attrs => attrs.width ? { style: `width: ${attrs.width}px` } : {},
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

// ── Toolbar button ────────────────────────────────────────────────────────────

function ToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      title={title}
      className={`pe-toolbar-btn${active ? ' pe-toolbar-btn--active' : ''}`}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
    >
      {children}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ParagonEditor({ value, onChange, onImageUpload, placeholder }) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      ResizableImage,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder ?? 'Write something…' }),
    ],
    content: value ?? '',
    onUpdate({ editor: e }) {
      onChange?.(e.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value ?? '', false);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  function setLink() {
    const prev = editor?.getAttributes('link').href ?? '';
    const url = window.prompt('URL', prev);
    if (url === null) return;
    if (!url) {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file || !onImageUpload) return;
    e.target.value = '';
    try {
      const url = await onImageUpload(file);
      if (url) {
        editor?.chain().focus().setImage({ src: url }).createParagraphNear().run();
      }
    } catch {
      // image upload errors are surfaced by the caller's toast handler
    }
  }

  if (!editor) return null;

  const ch = () => editor.chain().focus();

  return (
    <ImageUploadContext.Provider value={onImageUpload ?? null}>
      <div className="pe-wrapper ann-editor">
        <div className="pe-toolbar">
          {/* Headings */}
          <ToolbarBtn onClick={() => ch().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">H1</ToolbarBtn>
          <ToolbarBtn onClick={() => ch().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">H2</ToolbarBtn>
          <ToolbarBtn onClick={() => ch().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">H3</ToolbarBtn>

          <span className="pe-toolbar-sep" />

          {/* Inline marks */}
          <ToolbarBtn onClick={() => ch().toggleBold().run()}      active={editor.isActive('bold')}      title="Bold"><strong>B</strong></ToolbarBtn>
          <ToolbarBtn onClick={() => ch().toggleItalic().run()}    active={editor.isActive('italic')}    title="Italic"><em>I</em></ToolbarBtn>
          <ToolbarBtn onClick={() => ch().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline"><u>U</u></ToolbarBtn>
          <ToolbarBtn onClick={() => ch().toggleStrike().run()}    active={editor.isActive('strike')}    title="Strikethrough"><s>S</s></ToolbarBtn>

          <span className="pe-toolbar-sep" />

          {/* Lists */}
          <ToolbarBtn onClick={() => ch().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list">1.</ToolbarBtn>
          <ToolbarBtn onClick={() => ch().toggleBulletList().run()}  active={editor.isActive('bulletList')}  title="Bullet list">•</ToolbarBtn>

          <span className="pe-toolbar-sep" />

          {/* Block / inline elements */}
          <ToolbarBtn onClick={() => ch().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">"</ToolbarBtn>
          <ToolbarBtn onClick={setLink}                             active={editor.isActive('link')}       title="Link">🔗</ToolbarBtn>

          {onImageUpload && (
            <ToolbarBtn onClick={() => fileInputRef.current?.click()} active={false} title="Insert image">🖼</ToolbarBtn>
          )}

          <span className="pe-toolbar-sep" />

          {/* Clear formatting */}
          <ToolbarBtn onClick={() => ch().clearNodes().unsetAllMarks().run()} active={false} title="Clear formatting">✕</ToolbarBtn>
        </div>

        <EditorContent editor={editor} />

        {onImageUpload && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        )}
      </div>
    </ImageUploadContext.Provider>
  );
}
